import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import { PartPicker } from "./MovementDialog";
import { money, qty as fq } from "./stockUtils";

/** Create a draft parts PO, or edit an existing draft's lines/supplier. */
export function PartsPoFormDialog({ open, po, options, parts, onClose, onSaved }) {
  const [f, setF] = useState({ supplier_id: "", deliver_to_location_id: "", notes: "", vat_enabled: true, lines: [] });
  const [pick, setPick] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    const store = (options?.locations || []).find((l) => l.type === "store") || (options?.locations || [])[0];
    setF(po ? { supplier_id: po.supplier_id || "", deliver_to_location_id: po.deliver_to_location_id || "", notes: po.notes || "", vat_enabled: po.vat_enabled !== false, lines: (po.lines || []).map((l) => ({ ...l })) }
           : { supplier_id: "", deliver_to_location_id: store?.id || "", notes: "", vat_enabled: true, lines: [] });
    setPick("");
  }, [open, po, options]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setF((p) => ({ ...p, lines: p.lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  const addLine = (partId) => {
    const p = parts.find((x) => x.id === partId);
    if (!p) return;
    setF((prev) => prev.lines.some((l) => l.part_id === p.id) ? prev : { ...prev, lines: [...prev.lines, { part_id: p.id, part_name: p.name, part_number: p.part_number, unit: p.unit, qty: p.suggested_qty || p.reorder_qty || 1, unit_cost: p.unit_cost || 0 }] });
    setPick("");
  };
  const subtotal = f.lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.unit_cost || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...f, lines: f.lines.map((l) => ({ id: l.id, part_id: l.part_id, qty: Number(l.qty), unit_cost: Number(l.unit_cost), received_qty: l.received_qty || 0 })) };
      const { data } = po ? await API.put(`/stock/pos/${po.id}`, body) : await API.post("/stock/pos", body);
      toast.success(po ? "Purchase order updated" : `${data.po_number} drafted`);
      onSaved?.(data); onClose();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't save the purchase order"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="parts-po-form-dialog">
        <DialogHeader><DialogTitle>{po ? `Edit ${po.po_number}` : "New parts purchase order"}</DialogTitle><DialogDescription>Order spares from a supplier. Received goods are booked straight into the delivery location.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Supplier</Label>
              <Select value={f.supplier_id || "none"} onValueChange={(v) => set("supplier_id", v === "none" ? "" : v)}><SelectTrigger data-testid="po-supplier"><SelectValue placeholder="Choose supplier" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{(options?.suppliers || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Deliver to</Label>
              <Select value={f.deliver_to_location_id || "none"} onValueChange={(v) => set("deliver_to_location_id", v === "none" ? "" : v)}><SelectTrigger data-testid="po-deliver-to"><SelectValue placeholder="Location" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Decide on receipt</SelectItem>{(options?.locations || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div>
            <Label>Lines</Label>
            {f.lines.length > 0 && (
              <div className="border border-border rounded-md divide-y divide-border mb-2" data-testid="po-lines">
                {f.lines.map((l, i) => (
                  <div key={l.id || l.part_id || i} className="grid grid-cols-[1fr_90px_110px_90px_32px] items-center gap-2 px-3 py-2 text-sm" data-testid={`po-line-${i}`}>
                    <div className="min-w-0"><p className="font-medium truncate">{l.part_name}</p><p className="text-[10px] text-muted-foreground font-mono">{l.part_number || "—"} · {l.unit}</p></div>
                    <Input type="number" min="0" step="any" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} className="h-8 text-right" data-testid={`po-line-qty-${i}`} />
                    <Input type="number" min="0" step="0.01" value={l.unit_cost} onChange={(e) => setLine(i, "unit_cost", e.target.value)} className="h-8 text-right" data-testid={`po-line-cost-${i}`} />
                    <p className="text-right font-mono text-xs">{money(Number(l.qty || 0) * Number(l.unit_cost || 0))}</p>
                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setF((p) => ({ ...p, lines: p.lines.filter((_, j) => j !== i) }))} data-testid={`po-line-remove-${i}`}><Trash size={14} /></Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <div className="flex-1"><PartPicker parts={parts} value={pick} onChange={setPick} testid="po-part-picker" /></div>
              <Button type="button" variant="outline" onClick={() => addLine(pick)} disabled={!pick} data-testid="po-add-line"><Plus size={14} className="mr-1" /> Add</Button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 items-start">
            <div><Label>Notes to supplier</Label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="po-notes" /></div>
            <div className="rounded-md border border-border p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{money(subtotal)}</span></div>
              <label className="flex items-center justify-between gap-2 text-muted-foreground"><span className="flex items-center gap-2"><input type="checkbox" checked={f.vat_enabled} onChange={(e) => set("vat_enabled", e.target.checked)} data-testid="po-vat" /> VAT 20%</span><span className="font-mono">{money(f.vat_enabled ? subtotal * 0.2 : 0)}</span></label>
              <div className="flex justify-between font-semibold border-t border-border pt-1"><span>Total</span><span className="font-mono" data-testid="po-total">{money(subtotal * (f.vat_enabled ? 1.2 : 1))}</span></div>
            </div>
          </div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving || f.lines.length === 0} data-testid="po-save">{saving ? "Saving…" : po ? "Save changes" : "Create draft"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export const lineQty = fq;
